import AddToCartButton from '../shared/AddToCartButton';
import AddProductButton from '../shared/AddProductButton';
import { buildImageUrl, LineasMeta, ImagenRecortada } from './ProductoMeta';
import { estaDisponible } from '../../utils/disponibilidad';

// Alto de la etiqueta "Vuelve pronto": 38px de 330 de tarjeta en la referencia
// serían ~20px en nuestros ~170; más grande, a petición.
const ALTO_ETIQUETA_OFF = 26;

// Alto del dibujo de la etiqueta del menú ("Bestseller", "Hot right now"):
// 22px de 330 en la referencia serían 13; un poco menos, a petición.
const ALTO_ETIQUETA_MENU = 11.5;

/**
 * Tarjeta del menú completo, a dos columnas. Es el diseño de la tarjeta de la
 * sección promocional (PromoProductCard) llevado a fondo blanco: foto, franja
 * clara con la porción y el tiempo, el "+" en la esquina de la franja, y
 * debajo precio, nombre, descripción y la etiqueta del menú.
 *
 * Etiqueta del menú (Product.menuTagId): "Bestseller", "Hot right now"… Son
 * etiquetas del catálogo con kind 'menu', aparte de la de la foto (tagId) y del
 * Bestseller de la sección promocional (isBestseller).
 *
 * No disponible (Product.availability distinto de 'Disponible'): foto
 * atenuada, la etiqueta "Vuelve pronto" en la esquina superior izquierda (imagen
 * del dashboard), solo la porción en la franja, textos en gris y sin "+".
 *
 * Props:
 *   - unavailableTagImage: imagen de la etiqueta "Vuelve pronto"
 *   - addButtonImage: imagen del botón Agregar (Business.menuAddButtonImage)
 */
export default function MenuProductCard({ product, onClick, unavailableTagImage, addButtonImage }) {
  const imagen = buildImageUrl(product.image);
  const etiqueta = product.tagId?.isActive && product.tagId?.image ? product.tagId : null;
  const etiquetaMenu = product.menuTagId?.isActive && product.menuTagId?.image ? product.menuTagId : null;
  const disponible = estaDisponible(product);

  // Div y no <button>: AddToCartButton trae sus propios botones dentro.
  return (
    <div onClick={onClick} className="min-w-0 cursor-pointer">
      <div className="relative">
        {/* Contorno gris finísimo alrededor de foto + franja, como en la
            referencia (1px de captura = medio pixel en pantalla). */}
        <div className={`rounded-2xl overflow-hidden border-[0.5px] border-menucard-border ${disponible ? 'bg-menucard-strip' : 'bg-menucard-strip-off'}`}>
          {/* Foto 6:5, medida en la referencia (330×280). */}
          <div className="relative aspect-[6/5] bg-gray-100">
            {imagen ? (
              <img
                src={imagen}
                alt={product.name}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder.svg'; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
                Sin imagen
              </div>
            )}

            {/* Velo blanco: así se ve "apagado" sin perder la foto. */}
            {!disponible && <div className="absolute inset-0 bg-white/45" aria-hidden="true" />}

            {etiqueta && (
              <img
                src={buildImageUrl(etiqueta.image)}
                alt={etiqueta.name}
                className={`absolute bottom-1 right-1 h-7 w-auto max-w-[75%] object-contain pointer-events-none ${disponible ? '' : 'opacity-60'}`}
                loading="lazy"
              />
            )}

            {/* Pegada a la esquina: se recorta su aire transparente para que el
                dibujo quede al ras, como en la referencia. */}
            {!disponible && unavailableTagImage && (
              <div className="absolute top-0 left-0 pointer-events-none">
                <ImagenRecortada url={buildImageUrl(unavailableTagImage)} alt="Vuelve pronto" alto={ALTO_ETIQUETA_OFF} />
              </div>
            )}
          </div>

          {/* Franja: porción y tiempo. En los no disponibles solo la porción. */}
          {/* Colores de la referencia: el tiempo (icono y texto) en ocre; el
              icono de la porción en verde solo cuando son piezas (personas y
              ml van grises como su texto). Los no disponibles, todo gris. */}
          <div className={`h-11 flex flex-col justify-center gap-0.5 pl-2.5 min-w-0 ${addButtonImage ? 'pr-[52%]' : 'pr-12'} ${disponible ? 'text-menucard-meta' : 'text-gray-400'}`}>
            <LineasMeta
              product={product}
              conTiempo={disponible}
              claseIconoPorcion={disponible && product.portionUnit === 'piezas' ? 'text-menucard-portion-icon' : ''}
              claseTiempo="text-menucard-time"
            />
          </div>
        </div>

        {/* Botón para agregar, en la esquina de la franja. Con imagen del
            dashboard va como el "ADD" de la referencia, pegado abajo a la
            derecha, montado entre la foto y la franja: allá ocupa el 40% del
            ancho; aquí la mitad, más grande a petición. Sin imagen, el mismo
            "+" que en la sección promocional. Los no disponibles no lo
            llevan: no se pueden pedir. */}
        {disponible && (addButtonImage ? (
          <div className="absolute right-0 bottom-0 w-1/2">
            <AddProductButton product={product} image={buildImageUrl(addButtonImage)} />
          </div>
        ) : (
          <AddToCartButton product={product} className="absolute right-1.5 bottom-1" />
        ))}
      </div>

      <div className="pt-2 px-0.5">
        <span className={`block text-[16px] font-bold tracking-tight ${disponible ? 'text-gray-900' : 'text-gray-400'}`}>
          ${product.price?.toFixed(0)}
        </span>
        <h3 className={`text-[15px] font-medium leading-snug line-clamp-2 ${disponible ? 'text-gray-800' : 'text-gray-400'}`}>
          {product.name}
        </h3>
        {product.description && (
          <p className={`mt-0.5 flex gap-1 text-[13px] leading-snug ${disponible ? 'text-gray-500' : 'text-gray-400'}`}>
            {/* Destello de cuatro puntas, el mismo de la referencia. */}
            <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true" className="flex-shrink-0 mt-[4px]">
              <path fill="currentColor" d="M6 0c.4 3.1 2.9 5.6 6 6-3.1.4-5.6 2.9-6 6-.4-3.1-2.9-5.6-6-6 3.1-.4 5.6-2.9 6-6Z" />
            </svg>
            <span className="line-clamp-2">{product.description}</span>
          </p>
        )}
        {disponible && etiquetaMenu && (
          <ImagenRecortada
            url={buildImageUrl(etiquetaMenu.image)}
            alt={etiquetaMenu.name}
            alto={ALTO_ETIQUETA_MENU}
            className="mt-1.5"
          />
        )}
      </div>
    </div>
  );
}
